import React, { useState } from 'react';

const BackIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <polyline points="15 18 9 12 15 6"></polyline>
    </svg>
);

interface GuidedFinderProps {
    onBack: () => void;
    onSearch: (query: string) => void;
}

interface Question {
    id: string;
    question: string;
    options: { value: string; label: string }[];
}

const questions: Question[] = [
    {
        id: 'usage',
        question: 'What will you primarily use the laptop for?',
        options: [
            { value: 'gaming', label: 'Gaming' },
            { value: 'work', label: 'Work & Productivity' },
            { value: 'creative', label: 'Creative Work (Video/Photo Editing)' },
            { value: 'student', label: 'Student & General Use' },
            { value: 'programming', label: 'Programming & Development' }
        ]
    },
    {
        id: 'budget',
        question: 'What is your budget range?',
        options: [
            { value: 'under-50k', label: 'Under ₹50,000' },
            { value: '50k-80k', label: '₹50,000 - ₹80,000' },
            { value: '80k-120k', label: '₹80,000 - ₹1,20,000' },
            { value: '120k-150k', label: '₹1,20,000 - ₹1,50,000' },
            { value: 'above-150k', label: 'Above ₹1,50,000' }
        ]
    },
    {
        id: 'portability',
        question: 'How important is portability?',
        options: [
            { value: 'very', label: 'Very Important (Lightweight & Thin)' },
            { value: 'moderate', label: 'Moderately Important' },
            { value: 'not', label: 'Not Important (Performance First)' }
        ]
    },
    {
        id: 'performance',
        question: 'What performance level do you need?',
        options: [
            { value: 'basic', label: 'Basic (Web browsing, Office work)' },
            { value: 'mid', label: 'Mid-range (Multitasking, Light gaming)' },
            { value: 'high', label: 'High Performance (Heavy tasks, Gaming)' },
            { value: 'premium', label: 'Premium (Professional workstation)' }
        ]
    }
];

export const GuidedFinder: React.FC<GuidedFinderProps> = ({ onBack, onSearch }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [answers, setAnswers] = useState<Record<string, string>>({});

    const currentQuestion = questions[currentStep];
    const isLastQuestion = currentStep === questions.length - 1;

    const handleOptionSelect = (value: string) => {
        const newAnswers = { ...answers, [currentQuestion.id]: value };
        setAnswers(newAnswers);

        if (isLastQuestion) {
            const query = buildQuery(newAnswers);
            onSearch(query);
        } else {
            setTimeout(() => {
                setCurrentStep(currentStep + 1);
            }, 300);
        }
    };

    const buildQuery = (answers: Record<string, string>): string => {
        const usageMap: Record<string, string> = {
            gaming: 'gaming',
            work: 'work and productivity',
            creative: 'video editing and creative work',
            student: 'student use and general tasks',
            programming: 'programming and development'
        };

        const budgetMap: Record<string, string> = {
            'under-50k': 'under ₹50,000',
            '50k-80k': 'between ₹50,000 and ₹80,000',
            '80k-120k': 'between ₹80,000 and ₹1,20,000',
            '120k-150k': 'between ₹1,20,000 and ₹1,50,000',
            'above-150k': 'above ₹1,50,000'
        };

        const portabilityMap: Record<string, string> = {
            very: 'lightweight and portable',
            moderate: 'balanced portability',
            not: 'performance-focused'
        };

        const performanceMap: Record<string, string> = {
            basic: 'basic performance',
            mid: 'mid-range performance',
            high: 'high performance',
            premium: 'premium workstation performance'
        };

        const usage = usageMap[answers.usage] || '';
        const budget = budgetMap[answers.budget] || '';
        const portability = portabilityMap[answers.portability] || '';
        const performance = performanceMap[answers.performance] || '';

        return `Best laptops for ${usage} with ${budget}, ${portability}, and ${performance}`;
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        } else {
            onBack();
        }
    };

    const progress = ((currentStep + 1) / questions.length) * 100;

    return (
        <div className="max-w-2xl mx-auto animate-fade-in">
            <button
                onClick={handleBack}
                className="flex items-center mb-6 text-sm font-semibold text-[var(--text-2)] hover:text-[var(--brand)] transition-colors"
            >
                <BackIcon />
                {currentStep > 0 ? 'Previous Question' : 'Back to Home'}
            </button>

            <div className="bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-2xl shadow-lg p-6 sm:p-8 lg:p-10">
                <div className="mb-6">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-semibold text-[var(--text-2)]">
                            Question {currentStep + 1} of {questions.length}
                        </span>
                        <span className="text-sm font-semibold text-[var(--brand)]">
                            {Math.round(progress)}%
                        </span>
                    </div>
                    <div className="w-full bg-[var(--surface-3)] rounded-full h-2.5">
                        <div
                            className="bg-[var(--brand)] h-2.5 rounded-full transition-all duration-500 ease-out"
                            style={{ width: `${progress}%` }}
                        ></div>
                    </div>
                </div>

                <h2 className="text-2xl sm:text-3xl font-bold text-[var(--text-1)] mb-8 font-serif">
                    {currentQuestion.question}
                </h2>

                <div className="space-y-3">
                    {currentQuestion.options.map((option) => (
                        <button
                            key={option.value}
                            onClick={() => handleOptionSelect(option.value)}
                            className={`w-full text-left p-5 rounded-xl border-2 transition-all duration-200 ${
                                answers[currentQuestion.id] === option.value
                                    ? 'border-[var(--brand)] bg-[var(--brand)]/5'
                                    : 'border-[var(--surface-4)] hover:border-[var(--brand)] hover:bg-[var(--surface-3)]'
                            }`}
                        >
                            <div className="flex items-center justify-between">
                                <span className="font-semibold text-[var(--text-1)] text-base sm:text-lg">
                                    {option.label}
                                </span>
                                {answers[currentQuestion.id] === option.value && (
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-6 w-6 text-[var(--brand)]"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth={2.5}
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                )}
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};
